import { isDecimalString } from "@/shared/utils/decimal";

import type {
  CreateOtcQuotePayload,
  OtcAmountAnchor,
  OtcAmountBounds,
  OtcPair,
} from "../types/cryptoSwaps.types";
import { checkAmount, resolvePairBounds, type AmountBounds, type AmountCheck } from "./cryptoSwapAmounts";
import { quoteAmountPayload } from "./cryptoSwapConverter";

const nonNegative = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed && isDecimalString(trimmed) && !trimmed.startsWith("-") ? trimmed : null;
};

export function boundsOf(limits: OtcAmountBounds | null | undefined): AmountBounds | null {
  const min = nonNegative(limits?.min_amount);
  const max = nonNegative(limits?.max_amount);
  return min || max ? { min, max } : null;
}

export function sendBoundsOf(pair: Pick<OtcPair, "send_limits"> | null | undefined): AmountBounds | null {
  return boundsOf(pair?.send_limits);
}

export function receiveBoundsOf(
  pair: Pick<OtcPair, "receive_limits" | "limits"> | null | undefined,
): AmountBounds | null {
  return boundsOf(pair?.receive_limits) ?? resolvePairBounds(pair?.limits);
}

export interface AmountGateInput {
  anchor: OtcAmountAnchor;
  amount: string;
  sendDecimals: number;
  receiveDecimals: number;
  sendBounds: AmountBounds | null;
  receiveBounds: AmountBounds | null;
}

/** Each side answers only to its own limits: 50.000 COP is a valid order even when it buys under 20 USDT. */
export function amountGate(input: AmountGateInput): AmountCheck {
  return input.anchor === "send"
    ? checkAmount(input.amount, { decimals: input.sendDecimals, bounds: input.sendBounds })
    : checkAmount(input.amount, { decimals: input.receiveDecimals, bounds: input.receiveBounds });
}

/** The only door to a quote request: `null` for any amount the gate refuses. */
export function gatedQuoteAmount(
  input: AmountGateInput,
): Pick<CreateOtcQuotePayload, "send_amount" | "receive_amount"> | null {
  return amountGate(input) === "ok" ? quoteAmountPayload(input.anchor, input.amount) : null;
}
